import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import ProductCard from "@/components/storefront/ProductCard";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { ArrowRight, ArrowLeft, Sparkles, FileText, Clock, CheckCircle2 } from "lucide-react";

export default function Home() {
  const { t, lang } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim().toLowerCase();

  useEffect(() => {
    let mounted = true;
    api.get("/products").then((res) => {
      if (mounted) {
        setProducts(res.data || []);
        setLoading(false);
      }
    }).catch(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!q) return products;
    return products.filter((p) =>
      (p.name_en + " " + p.name_ar + " " + p.category_en + " " + p.category_ar)
        .toLowerCase()
        .includes(q)
    );
  }, [products, q]);

  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="hero-noise absolute inset-0 opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#FFD60A] text-black border border-black/10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" />
              {t.tagline}
            </div>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] whitespace-pre-line">
              {t.heroTitle}
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-md leading-relaxed">
              {t.heroSub}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-full bg-[#FFD60A] text-black hover:bg-[#FFD60A]/90 gap-2 font-bold"
                onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-shop-now"
              >
                {t.shopNow}
                <Arrow className="h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-explore"
              >
                {t.explore}
              </Button>
            </div>
            <div className="flex items-center gap-4 pt-4 text-xs text-white/70">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" />ATS-friendly</span>
              <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Word + PDF</span>
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />Instant download</span>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-floating border border-white/10 bg-white">
              <img
                src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=1100&q=80"
                alt="Resume preview"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -start-4 bg-surface text-foreground rounded-xl p-4 shadow-floating border border-border max-w-[220px]">
              <p className="text-xs text-muted-foreground uppercase tracking-widest">{t.features}</p>
              <p className="text-sm font-semibold mt-1">{t.f3Title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.f3Desc}</p>
            </div>
            <div className="absolute -top-3 -end-3 bg-[#FFD60A] text-black rounded-xl px-3 py-2 shadow-floating">
              <p className="text-[10px] uppercase font-bold tracking-widest">{lang === "ar" ? "من" : "From"}</p>
              <p className="text-lg font-extrabold leading-none">15 {lang === "ar" ? "ر.س" : "SAR"}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Top categories — Resume vs Courses */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 w-full">
        <div className="grid sm:grid-cols-2 gap-5">
          <a
            href="#products"
            onClick={(e) => { e.preventDefault(); document.getElementById("products")?.scrollIntoView({behavior:"smooth"}); }}
            data-testid="category-card-resume"
            className="group relative overflow-hidden rounded-2xl border border-border bg-surface hover:shadow-floating transition-all p-6 flex items-center gap-5"
          >
            <div className="h-20 w-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
              <img src="https://customer-assets.emergentagent.com/job_merchant-hub-439/artifacts/shntq95y_409a8764-2e03-4af4-a99c-bc0bea1255f6-thumbnail-1000x1000.png" alt="Resume" className="h-full w-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{t.allProducts}</p>
              <h3 className="text-2xl font-bold mt-1">{lang === "ar" ? "السير الذاتية" : "Resumes"}</h3>
              <p className="text-sm text-muted-foreground mt-1">{lang === "ar" ? "٤ قوالب احترافية" : "4 professional templates"}</p>
            </div>
            <Arrow className="h-5 w-5 text-primary opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition" />
          </a>
          <Link
            to="/courses"
            data-testid="category-card-courses"
            className="group relative overflow-hidden rounded-2xl border border-border bg-surface hover:shadow-floating transition-all p-6 flex items-center gap-5"
          >
            <div className="h-20 w-20 rounded-md overflow-hidden bg-muted flex-shrink-0">
              <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=600&q=80" alt="Courses" className="h-full w-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{t.courses}</p>
              <h3 className="text-2xl font-bold mt-1">{lang === "ar" ? "دورات اللغة" : "Language courses"}</h3>
              <p className="text-sm text-muted-foreground mt-1">1 {lang === "ar" ? "ر.س / دقيقة · ١:١ مباشر" : "SAR / minute · 1-on-1 live"}</p>
            </div>
            <Arrow className="h-5 w-5 text-primary opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex-1 w-full">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.allProducts}</p>
            <h2 className="text-2xl sm:text-3xl font-bold mt-1">
              {lang === "ar" ? "قوالب السير الذاتية" : "CV & Resume templates"}
            </h2>
          </div>
          <span className="text-sm text-muted-foreground" data-testid="product-count">
            {filtered.length} {lang === "ar" ? "قالب" : "templates"}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-surface rounded-xl border border-border h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div data-testid="products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* Features section removed per user request — chips inside hero are enough */}

      <Footer />
    </div>
  );
}

const FeatureCard = ({ icon, title, desc }) => (
  <div className="flex gap-4 items-start">
    <div className="h-11 w-11 rounded-xl bg-[#FFD60A]/20 text-primary flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div>
      <h4 className="font-bold text-base">{title}</h4>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{desc}</p>
    </div>
  </div>
);
